/**
 * Case-time event log for the segmentation study route (ENG-202).
 *
 * Mounted once in LabelingPage, above the per-study CaseViewer (which remounts
 * on every case), so a case change doesn't drop queued events. Wraps the
 * framework-free `CaseEventQueue` with the page's listeners and stop points:
 *
 * - `case_open` once the case is known to be open, on every case change, and
 *   again when a closed case reopens (Unmark);
 * - `activity` from any input (throttled in the queue), not while the tab is
 *   hidden;
 * - `tab_hidden` / `tab_visible` on visibilitychange;
 * - none of these while the case is closed or its status is still unknown;
 * - sends every 15s while something is queued, with keepalive on tab hide and
 *   pagehide, and on case change / unmount.
 *
 * `page_session_id` is new per mount (not per page load): leaving for the
 * worklist and coming back is a new session, so that visit is never paired
 * into a counted gap on the case.
 *
 * Returns null when disabled; consumers read it through `CaseEventLogContext`
 * and do nothing when it is null (other project types, factory, read-only).
 */
import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { recordCaseEvents } from "../services/auth";
import {
  CaseEventQueue,
  type ClientCaseEventType,
  type KnownCaseEvent,
  type ServerCaseEventType,
  type TimeEventStamp,
} from "../utils/caseEventQueue";

/** Same list as useIdleTimer, plus pointer/touch moves so a long stylus drag isn't idle. */
const INPUT_EVENTS = [
  "mousemove",
  "mousedown",
  "wheel",
  "keydown",
  "click",
  "touchstart",
  "pointermove",
  "pointerdown",
  "touchmove",
] as const;

export const FLUSH_INTERVAL_MS = 15_000;
/** Mark Complete / Unassessable wait at most this long for the flush. */
export const FLUSH_BEFORE_TIMEOUT_MS = 2_000;

export interface CaseEventLog {
  readonly pageSessionId: string;
  /** Queue an annotation event (annotation_start / annotation_cancel). */
  add: (
    type: Extract<ClientCaseEventType, "annotation_start" | "annotation_cancel">,
    studyUid: string,
    extra: { annotation_session_id: string; edited_annotation_id?: string; at?: string },
  ) => void;
  /** `time_event` for an action whose event the backend writes. */
  stamp: (
    type: ServerCaseEventType,
    studyUid: string,
    extra?: { annotation_session_id?: string },
  ) => TimeEventStamp | null;
  /** Forget a stamp whose action failed. */
  discardStamp: (studyUid: string, eventId: string) => void;
  /** Events this page stamped or queued for a study (for the live stretch). */
  knownEvents: (studyUid: string) => KnownCaseEvent[];
  flush: () => Promise<void>;
  /** Flush, but never wait longer than FLUSH_BEFORE_TIMEOUT_MS. */
  flushBefore: () => Promise<void>;
  isHidden: () => boolean;
}

export const CaseEventLogContext = createContext<CaseEventLog | null>(null);

export function useCaseEventLogContext(): CaseEventLog | null {
  return useContext(CaseEventLogContext);
}

export interface UseCaseEventLogOptions {
  enabled: boolean;
  projectId: string;
  /** Current case; "" while loading (no events). */
  studyUid: string;
  userId: string | null;
  /**
   * Complete or unassessable; null while the case status isn't known yet.
   * Only an open case gets events: a case closed before the event log has a
   * marker but no mark_complete event, so any case_open / tab / activity pair
   * on it would count. case_open is sent once the case is known to be open,
   * and again after it reopens (Unmark).
   */
  caseClosed: boolean | null;
}

export function useCaseEventLog({
  enabled,
  projectId,
  studyUid,
  userId,
  caseClosed,
}: UseCaseEventLogOptions): CaseEventLog | null {
  const [queue, setQueue] = useState<CaseEventQueue | null>(null);
  const studyRef = useRef(studyUid);
  studyRef.current = studyUid;
  const caseOpen = caseClosed === false;
  const openRef = useRef(caseOpen);
  openRef.current = caseOpen;

  // One queue (and page_session_id) per mount / project / user.
  useEffect(() => {
    if (!enabled) return;
    const created = new CaseEventQueue({
      projectId,
      pageSessionId: crypto.randomUUID(),
      userId,
      send: recordCaseEvents,
    });
    setQueue(created);
    void created.restore();
    return () => {
      created.settleActivity();
      void created.flush();
      created.dispose();
      setQueue(null);
    };
  }, [enabled, projectId, userId]);

  // case_open per case once it is known to be open (and again on reopen);
  // flush that case's events when it changes or closes.
  useEffect(() => {
    if (!queue || !studyUid || !caseOpen) return;
    queue.add("case_open", studyUid);
    return () => {
      queue.settleActivity();
      void queue.flush();
    };
  }, [queue, studyUid, caseOpen]);

  // Input, visibility, pagehide and the periodic send.
  useEffect(() => {
    if (!queue) return;
    const handleInput = () => {
      const study = studyRef.current;
      if (!study || document.hidden || !openRef.current) return;
      queue.input(study);
    };
    const handleVisibility = () => {
      const study = studyRef.current;
      if (!study || !openRef.current) return;
      if (document.hidden) {
        queue.add("tab_hidden", study);
        void queue.flush({ keepalive: true });
      } else {
        queue.add("tab_visible", study);
      }
    };
    const handlePageHide = () => {
      queue.settleActivity();
      void queue.flush({ keepalive: true });
    };
    for (const ev of INPUT_EVENTS) {
      // passive + capture like useIdleTimer, so scrolling isn't slowed.
      document.addEventListener(ev, handleInput, { passive: true, capture: true });
    }
    document.addEventListener("visibilitychange", handleVisibility);
    window.addEventListener("pagehide", handlePageHide);
    const interval = window.setInterval(() => {
      if (queue.size > 0) void queue.flush();
    }, FLUSH_INTERVAL_MS);
    return () => {
      for (const ev of INPUT_EVENTS) {
        document.removeEventListener(ev, handleInput, { capture: true });
      }
      document.removeEventListener("visibilitychange", handleVisibility);
      window.removeEventListener("pagehide", handlePageHide);
      window.clearInterval(interval);
    };
  }, [queue]);

  return useMemo<CaseEventLog | null>(() => {
    if (!queue) return null;
    return {
      pageSessionId: queue.pageSessionId,
      add: (type, study, extra) => void queue.add(type, study, extra),
      stamp: (type, study, extra) => queue.stamp(type, study, extra),
      discardStamp: (study, eventId) => queue.discardStamp(study, eventId),
      knownEvents: (study) => queue.knownEvents(study),
      flush: () => {
        queue.settleActivity();
        return queue.flush();
      },
      flushBefore: () => {
        queue.settleActivity();
        return Promise.race([
          queue.flush(),
          new Promise<void>((resolve) => setTimeout(resolve, FLUSH_BEFORE_TIMEOUT_MS)),
        ]);
      },
      isHidden: () => document.hidden,
    };
  }, [queue]);
}
